import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { Globe, Search, BarChart3, Key, Code, RefreshCw, Link as LinkIcon, Eye, FlaskConical, MousePointerClick, Zap, ShieldCheck, Clock, Unlink, Image as ImageIcon, Smartphone, AlertTriangle, UserX, Database, HelpCircle, CheckCircle2, XCircle, ArrowRight, Plug, Gift, Sparkles, Server, LayoutTemplate, Activity, ArrowUpRight } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const painPoints = [{
  icon: <Clock size={28} data-id="element-2310" />,
  title: 'The Developer Bottleneck',
  desc: 'You need a simple text change or a new landing page, but you have to wait two weeks for your web guy to get around to it.'
}, {
  icon: <HelpCircle size={28} data-id="element-2311" />,
  title: 'The SEO Black Box',
  desc: "You're paying an agency for 'SEO' every month, but you have no idea what they actually do or if it's even working."
}, {
  icon: <AlertTriangle size={28} data-id="element-2312" />,
  title: 'The Plugin Nightmare',
  desc: "You click 'update' on a simple plugin and suddenly half your website is broken. Now you're scrambling to fix it."
}, {
  icon: <UserX size={28} data-id="element-2313" />,
  title: 'The Stale Content Trap',
  desc: 'Your last blog post was from 2022. Your services have changed, but your website still tells the old story.'
}];
const platforms = [{
  name: 'WordPress',
  domain: 'wordpress.org'
}, {
  name: 'Shopify',
  domain: 'shopify.com'
}, {
  name: 'Webflow',
  domain: 'webflow.com'
}, {
  name: 'Wix',
  domain: 'wix.com'
}, {
  name: 'Squarespace',
  domain: 'squarespace.com'
}, {
  name: 'GoHighLevel',
  domain: 'gohighlevel.com'
}, {
  name: 'Ghost',
  domain: 'ghost.org'
}, {
  name: 'Magento',
  domain: 'magento.com'
}];
const hostingBenefits = ['Enterprise-grade cloud hosting', 'Daily automated backups', 'Free SSL certificates', 'Global CDN for lightning speed', 'Built-in visual page builder', 'Unlimited bandwidth & storage'];
const features = [{
  icon: <Globe size={22} data-id="element-2314" />,
  title: 'Auto Page Creation',
  desc: 'MR spots a new trend or keyword and automatically builds a fully optimized landing page to capture that traffic.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Search size={22} data-id="element-2315" />,
  title: 'On-Page SEO',
  desc: 'Titles, descriptions, headers, and tags are constantly tweaked and optimized to keep you ranking high.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <FlaskConical size={22} data-id="element-2316" />,
  title: 'A/B Split Testing',
  desc: 'MR tests different headlines, buttons, and layouts against each other to find out what actually gets people to buy.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Eye size={22} data-id="element-2317" />,
  title: 'Competitor Watch',
  desc: 'If a competitor changes their pricing or launches a new feature, MR sees it and alerts you instantly.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Zap size={22} data-id="element-2318" />,
  title: 'Speed Optimization',
  desc: 'MR constantly compresses images, minifies code, and tweaks settings so your site loads in a blink.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <ShieldCheck size={22} data-id="element-2319" />,
  title: 'Security & Patching',
  desc: 'Hackers never sleep, and neither does MR. Vulnerabilities are patched before they become problems.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <RefreshCw size={22} data-id="element-2320" />,
  title: 'Content Refreshing',
  desc: 'MR finds old, outdated blog posts and updates them with fresh stats and info to bring them back to life.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <LinkIcon size={22} data-id="element-2321" />,
  title: 'Smart Internal Linking',
  desc: 'As new pages are built, MR automatically links them to your existing content to boost your SEO authority.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Code size={22} data-id="element-2322" />,
  title: 'Schema Markup',
  desc: 'MR adds the hidden code that helps Google understand your business, getting you those rich search results.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Activity size={22} data-id="element-2323" />,
  title: 'Uptime Monitoring',
  desc: 'MR checks your site every 60 seconds. If it goes down, MR starts fixing it before you even notice.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <ImageIcon size={22} data-id="element-2324" />,
  title: 'Image Management',
  desc: 'Every image is automatically resized, compressed, and given the right alt-text for visually impaired users and SEO.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Smartphone size={22} data-id="element-2325" />,
  title: 'Mobile Perfect',
  desc: 'MR ensures every new page looks flawless on phones, tablets, and desktops without you having to check.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <Key size={22} data-id="element-2326" />,
  title: 'Keyword Tracking',
  desc: 'Watch your rankings climb. MR tracks exactly where you stand for the searches that matter to your business.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <Unlink size={22} data-id="element-2327" />,
  title: 'Broken Link Fixing',
  desc: 'Nothing hurts SEO like a 404 error. MR finds broken links and redirects them automatically.',
  color: 'bg-purple/10 text-purple'
}, {
  icon: <MousePointerClick size={22} data-id="element-2328" />,
  title: 'Conversion Tracking',
  desc: 'Know exactly where your leads come from. MR tracks every click, form fill, and phone call.',
  color: 'bg-teal/10 text-teal'
}, {
  icon: <BarChart3 size={22} data-id="element-2329" />,
  title: 'Plain English Reports',
  desc: 'No confusing charts. Just a simple weekly summary of how much traffic you got and what MR did to improve it.',
  color: 'bg-purple/10 text-purple'
}];
const withoutMr = ['Waiting weeks for a developer to make simple text changes', 'Paying thousands for SEO without knowing what they do', "Website goes down and you don't know until a customer complains", 'Plugins break your site when you try to update them', 'Old, outdated content making your business look abandoned', 'No idea why people are leaving your site without buying'];
const withMr = ['Changes happen instantly just by asking', 'SEO is handled automatically, 24/7, with clear results', 'Site is monitored every 60 seconds and auto-healed', 'Updates are tested safely before going live', 'Content is constantly refreshed and expanded', 'A/B tests run automatically to get you more customers'];
export function SelfBuildingWebsite() {
  return <div className="w-full bg-white" data-id="element-2330">
      <Nav data-id="element-2331" />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative bg-[#0F1D3A] pt-44 pb-24 px-6 overflow-hidden" data-id="element-2332">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple/15 rounded-full blur-[120px] pointer-events-none" data-id="element-2333" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal/10 rounded-full blur-[100px] pointer-events-none" data-id="element-2334" />

        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-2335">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2336">
            <motion.span variants={fadeUp} className="inline-block text-teal text-sm font-bold tracking-widest uppercase mb-4" data-id="element-2337">
              Self-Building Website
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6" data-id="element-2338">
              Your Website. <br className="hidden md:block" data-id="element-2339" />
              <span className="text-gradient" data-id="element-2340">Finally Building Itself.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-2341">
              Stop paying developers for simple changes. Stop wondering if your
              SEO is working. MR builds pages, writes content, runs split tests,
              and keeps everything secure — 24/7.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-2342">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-2343">
                Talk to MR
              </Link>
              <Link to="/how-it-works" className="btn-ghost hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-shadow" data-id="element-2344">
                See How It Works
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6" data-id="element-2345">
              {[{
              value: '24/7',
              label: 'Monitoring & Security'
            }, {
              value: '100+',
              label: 'SEO Factors Checked'
            }, {
              value: 'Zero',
              label: 'Developer Fees'
            }].map(stat => <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl px-6 py-3 text-center" data-id="element-2346">
                  <div className="text-2xl font-bold text-white" data-id="element-2347">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400 font-medium" data-id="element-2348">
                    {stat.label}
                  </div>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ PAIN SECTION ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2349">
        <div className="max-w-6xl mx-auto" data-id="element-2350">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2351">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2352">
              Sound Familiar?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2353">
              If managing your website feels like a constant headache of broken
              plugins and expensive developers, you're not alone.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-6" data-id="element-2354">
            {painPoints.map(p => <motion.div key={p.title} variants={fadeUp} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2355">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 mb-4" data-id="element-2356">
                  {p.icon}
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2" data-id="element-2357">
                  {p.title}
                </h3>
                <p className="text-gray-500 leading-relaxed" data-id="element-2358">{p.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ VISUAL GRAPHIC: YOUR WEBSITE HAS A BRAIN ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2359">
        <div className="max-w-5xl mx-auto" data-id="element-2360">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={fadeUp} className="relative bg-gradient-to-br from-[#0F1D3A] to-[#1A447A] rounded-2xl p-10 md:p-14 overflow-hidden" data-id="element-2361">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-teal/20 rounded-full blur-[100px] pointer-events-none" data-id="element-2362" />
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-purple/20 rounded-full blur-[100px] pointer-events-none" data-id="element-2363" />

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-12" data-id="element-2364">
              <div className="flex-1" data-id="element-2365">
                <div className="inline-flex items-center gap-2 bg-teal/20 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-2366">
                  <Activity size={16} data-id="element-2367" /> Live Optimization Engine
                </div>
                <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2368">
                  Your Website Finally Has a Brain.
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-2369">
                  Most websites just sit there waiting for you to do something.
                  MR turns your site into a living, breathing engine that
                  constantly improves itself.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed" data-id="element-2370">
                  It watches how visitors behave, tests new layouts, fixes
                  broken links, and creates new content based on what people are
                  actually searching for —{' '}
                  <strong className="text-white" data-id="element-2371">all in real-time.</strong>
                </p>
              </div>

              {/* Custom Graphic UI */}
              <div className="w-full md:w-[45%] relative" data-id="element-2372">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple/10 to-transparent animate-pulse rounded-2xl" data-id="element-2373" />

                <div className="flex flex-col gap-4 relative z-10" data-id="element-2374">
                  {/* Metric Card 1 */}
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex items-center gap-4 transform translate-x-4" data-id="element-2375">
                    <div className="w-10 h-10 rounded-lg bg-teal/20 flex items-center justify-center text-teal" data-id="element-2376">
                      <ArrowUpRight size={20} data-id="element-2377" />
                    </div>
                    <div data-id="element-2378">
                      <div className="text-xs text-gray-400 font-medium" data-id="element-2379">
                        Conversion Rate
                      </div>
                      <div className="text-lg font-bold text-white flex items-center gap-2" data-id="element-2380">
                        4.2%{' '}
                        <span className="text-xs text-teal bg-teal/10 px-2 py-0.5 rounded-full" data-id="element-2381">
                          +1.2%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Central MR Action */}
                  <div className="bg-gradient-to-r from-purple/20 to-teal/20 backdrop-blur-md rounded-xl p-5 border border-white/20 shadow-[0_0_30px_rgba(124,58,237,0.2)] z-20" data-id="element-2382">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2383">
                      <Sparkles size={18} className="text-purple" data-id="element-2384" />
                      <span className="text-sm font-bold text-white" data-id="element-2385">
                        MR Action Executed
                      </span>
                    </div>
                    <div className="text-sm text-gray-300" data-id="element-2386">
                      "A/B test concluded. Variant B increased signups by 18%.
                      Variant B is now live for all users."
                    </div>
                  </div>

                  {/* Metric Card 2 */}
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex items-center gap-4 transform -translate-x-4" data-id="element-2387">
                    <div className="w-10 h-10 rounded-lg bg-purple/20 flex items-center justify-center text-purple" data-id="element-2388">
                      <Search size={20} data-id="element-2389" />
                    </div>
                    <div data-id="element-2390">
                      <div className="text-xs text-gray-400 font-medium" data-id="element-2391">
                        New Keyword Found
                      </div>
                      <div className="text-sm font-bold text-white" data-id="element-2392">
                        "AI Business Automation"
                      </div>
                      <div className="text-xs text-teal mt-1" data-id="element-2393">
                        ✓ Landing page generated
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ HOW IT ACTUALLY WORKS (TIMELINE) ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2394">
        <div className="max-w-5xl mx-auto" data-id="element-2395">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-16" data-id="element-2396">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2397">
              How It Actually Works
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2398">
              Let's look at a real-world scenario. Here's what happens when a
              competitor tries to outrank you.
            </motion.p>
          </motion.div>

          <div className="relative" data-id="element-2399">
            {/* Connecting Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple via-teal to-purple opacity-20 hidden md:block" data-id="element-2400" />

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger} className="space-y-12" data-id="element-2401">
              {/* Step 1 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2402">
                <div className="md:w-1/2 flex justify-end" data-id="element-2403">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2404">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2405" />
                    <div className="text-sm font-bold text-gray-500 mb-2" data-id="element-2406">
                      Step 1: The Threat
                    </div>
                    <p className="text-gray-800 text-sm font-medium" data-id="element-2407">
                      A competitor publishes a massive new guide on "Local
                      Marketing" and starts stealing your search traffic.
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-sm z-10 shadow-sm hidden md:flex" data-id="element-2408">
                  1
                </div>
                <div className="md:w-1/2" data-id="element-2409" />
              </motion.div>

              {/* Step 2 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2410">
                <div className="md:w-1/2" data-id="element-2411" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2412">
                  2
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2413">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2414">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-white border-b border-l border-gray-100 rotate-45 hidden md:block" data-id="element-2415" />
                    <div className="text-sm font-bold text-purple mb-2 flex items-center gap-2" data-id="element-2416">
                      <Eye size={14} data-id="element-2417" /> Step 2: MR Detects
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2418">
                      MR's competitor monitoring spots the new page, analyzes
                      its keywords, and identifies exactly why it's ranking.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2419">
                <div className="md:w-1/2 flex justify-end" data-id="element-2420">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 w-full md:w-[400px] relative" data-id="element-2421">
                    <div className="absolute top-6 -right-3 w-6 h-6 bg-white border-t border-r border-gray-100 rotate-45 hidden md:block" data-id="element-2422" />
                    <div className="text-sm font-bold text-teal mb-2 flex items-center gap-2" data-id="element-2423">
                      <Code size={14} data-id="element-2424" /> Step 3: MR Responds
                    </div>
                    <p className="text-gray-600 text-sm" data-id="element-2425">
                      MR automatically drafts a better, more comprehensive page
                      for your site, complete with optimized images and schema
                      markup.
                    </p>
                  </div>
                </div>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2426">
                  3
                </div>
                <div className="md:w-1/2" data-id="element-2427" />
              </motion.div>

              {/* Step 4 */}
              <motion.div variants={fadeUp} className="relative flex flex-col md:flex-row items-center gap-8" data-id="element-2428">
                <div className="md:w-1/2" data-id="element-2429" />
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center font-bold text-sm z-10 shadow-lg hidden md:flex" data-id="element-2430">
                  4
                </div>
                <div className="md:w-1/2 flex justify-start" data-id="element-2431">
                  <div className="bg-purple/5 p-6 rounded-xl shadow-sm border border-purple/20 w-full md:w-[400px] relative" data-id="element-2432">
                    <div className="absolute top-6 -left-3 w-6 h-6 bg-[#f9f5ff] border-b border-l border-purple/20 rotate-45 hidden md:block" data-id="element-2433" />
                    <div className="text-sm font-bold text-purple mb-2 flex items-center gap-2" data-id="element-2434">
                      <Sparkles size={14} data-id="element-2435" /> Step 4: You Win
                    </div>
                    <p className="text-gray-700 text-sm italic" data-id="element-2436">
                      "Hey! I noticed a competitor trying to outrank us for
                      Local Marketing. I built a better page, published it, and
                      we've already reclaimed the #1 spot."
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ WORKS WITH ANY PLATFORM ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2437">
        <div className="max-w-5xl mx-auto" data-id="element-2438">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2439">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-teal/10 text-teal px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2440">
              <Plug size={16} data-id="element-2441" /> Works With Any Platform
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2442">
              We Integrate With What You Already Use.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2443">
              Already have a website you love? Keep it. MR plugs right into your
              existing CMS and makes it smarter, faster, and fully automated.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-id="element-2444">
            {platforms.map(tool => <motion.div key={tool.name} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 flex flex-col items-center gap-3 border border-gray-200 hover:border-purple/40 hover:shadow-md transition-all duration-300" data-id="element-2445">
                <img src={`https://logo.clearbit.com/${tool.domain}`} alt={tool.name} className="w-10 h-10 rounded-lg object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = `https://www.google.com/s2/favicons?domain=${tool.domain}&sz=128`;
            }} data-id="element-2446" />
                <span className="text-sm font-semibold text-gray-700" data-id="element-2447">
                  {tool.name}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ 1DASH INCLUDED ═══════════════ */}
      <section className="py-20 px-6 bg-[#0F1D3A]" data-id="element-2448">
        <div className="max-w-5xl mx-auto" data-id="element-2449">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2450">
            <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-2451">
              <span className="inline-flex items-center gap-2 bg-purple/10 text-purple px-4 py-1.5 rounded-full text-sm font-semibold mb-4" data-id="element-2452">
                <Gift size={16} data-id="element-2453" /> Included With Your Complete Package
              </span>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4" data-id="element-2454">
                Need a New Site? Get 1Dash —{' '}
                <span className="text-gradient" data-id="element-2455">On Us.</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-2" data-id="element-2456">
                If your current site is beyond repair, we've got you covered.
                When you go with our complete package, you get{' '}
                <strong className="text-white" data-id="element-2457">
                  1Dash Website Builder & Hosting
                </strong>{' '}
                included at no extra cost.
              </p>
              <p className="text-gray-500 text-sm" data-id="element-2458">
                Already love your site? That's totally fine — this is an option,
                not a requirement.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10" data-id="element-2459">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-8" data-id="element-2460">
                <div className="flex items-center gap-3" data-id="element-2461">
                  <div className="w-12 h-12 rounded-xl bg-purple/20 flex items-center justify-center" data-id="element-2462">
                    <LayoutTemplate size={24} className="text-purple" data-id="element-2463" />
                  </div>
                  <div data-id="element-2464">
                    <h3 className="text-xl font-poppins font-bold text-white" data-id="element-2465">
                      1Dash Website Builder
                    </h3>
                    <span className="text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-full" data-id="element-2466">
                      Enterprise Hosting Included
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2467">
                {hostingBenefits.map(b => <div key={b} className="flex items-start gap-3" data-id="element-2468">
                    <CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" data-id="element-2469" />
                    <span className="text-gray-300 text-sm" data-id="element-2470">{b}</span>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ FEATURES GRID ═══════════════ */}
      <section className="py-20 px-6 bg-white" data-id="element-2471">
        <div className="max-w-6xl mx-auto" data-id="element-2472">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2473">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2474">
              Everything MR Handles For You
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-2475">
              All the technical details, SEO tweaks, and content updates that
              usually take hours of your day — MR takes care of it.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-2476">
            {features.map(f => <motion.div key={f.title} variants={fadeUp} className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group" data-id="element-2477">
                <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-2478">
                  {f.icon}
                </div>
                <h3 className="font-poppins font-semibold text-gray-900 mb-2" data-id="element-2479">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2480">
                  {f.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ BEFORE & AFTER ═══════════════ */}
      <section className="py-20 px-6 bg-gray-50" data-id="element-2481">
        <div className="max-w-5xl mx-auto" data-id="element-2482">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center mb-14" data-id="element-2483">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-2484">
              The Difference Is Night and Day
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid md:grid-cols-2 gap-8" data-id="element-2485">
            {/* Without MR */}
            <motion.div variants={fadeUp} className="bg-red-50 border border-red-200 rounded-2xl p-8" data-id="element-2486">
              <h3 className="font-poppins font-bold text-xl text-red-700 mb-6 flex items-center gap-2" data-id="element-2487">
                <XCircle size={24} data-id="element-2488" /> Without MR
              </h3>
              <ul className="space-y-4" data-id="element-2489">
                {withoutMr.map(item => <li key={item} className="flex items-start gap-3 text-red-700/80" data-id="element-2490">
                    <XCircle size={16} className="shrink-0 mt-1 text-red-400" data-id="element-2491" />
                    <span className="text-sm leading-relaxed" data-id="element-2492">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            {/* With MR */}
            <motion.div variants={fadeUp} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8" data-id="element-2493">
              <h3 className="font-poppins font-bold text-xl text-emerald-700 mb-6 flex items-center gap-2" data-id="element-2494">
                <CheckCircle2 size={24} data-id="element-2495" /> With MR
              </h3>
              <ul className="space-y-4" data-id="element-2496">
                {withMr.map(item => <li key={item} className="flex items-start gap-3 text-emerald-700/80" data-id="element-2497">
                    <CheckCircle2 size={16} className="shrink-0 mt-1 text-emerald-500" data-id="element-2498" />
                    <span className="text-sm leading-relaxed" data-id="element-2499">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="py-24 px-6 bg-[#0F1D3A] text-center" data-id="element-2500">
        <div className="max-w-3xl mx-auto" data-id="element-2501">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-2502">
            <motion.h2 variants={fadeUp} className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-2503">
              Ready for a Website That Actually Works For You?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto" data-id="element-2504">
              Stop paying for simple updates and guessing if your SEO is
              working. Let MR handle it all.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-2505">
              <Link to="/contact" className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow text-lg px-10 py-4" data-id="element-2506">
                Talk to MR
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2507" />
    </div>;
}